import { useState } from 'react';
import { User, ExternalLink, Loader2, Check, X, AlertTriangle, Hand } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ApprovalRequestOut } from '@/lib/api';

interface ApprovalListProps {
  requests: ApprovalRequestOut[];
  isFull: boolean;
  onApprove: (requestId: string) => Promise<void>;
  onDecline: (requestId: string) => Promise<void>;
}

export function ApprovalList({ requests, isFull, onApprove, onDecline }: ApprovalListProps) {
  const [processingId, setProcessingId] = useState<string | null>(null);
  const [actionType, setActionType] = useState<'approve' | 'decline' | null>(null);

  const handleProfileClick = (userId: string) => {
    window.open(`/profile/${userId}`, '_blank');
  };

  const handleApprove = async (e: React.MouseEvent, requestId: string) => {
    e.stopPropagation();
    setProcessingId(requestId);
    setActionType('approve');
    try {
      await onApprove(requestId);
    } finally {
      setProcessingId(null);
      setActionType(null);
    }
  };

  const handleDecline = async (e: React.MouseEvent, requestId: string) => {
    e.stopPropagation();
    setProcessingId(requestId);
    setActionType('decline');
    try {
      await onDecline(requestId);
    } finally {
      setProcessingId(null);
      setActionType(null);
    }
  };

  if (requests.length === 0) {
    return (
      <div className="text-center py-8">
        <Check size={48} className="text-adv-mediumGray mx-auto mb-4" />
        <p className="text-adv-lightGray">No pending approval requests</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2 text-adv-lightGray text-sm pb-1 border-b border-adv-mediumGray/30">
        <Hand size={14} />
        <span>Pending Requests</span>
      </div>
      {isFull && (
        <div className="flex items-center gap-2 p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-lg text-yellow-400 text-sm">
          <AlertTriangle size={16} />
          <span>Ride is full. Approved riders will be added to the waitlist.</span>
        </div>
      )}
      {requests.map((request) => (
        <div
          key={request.id}
          className="flex items-center p-3 border border-adv-mediumGray rounded-lg hover:border-adv-orange transition-colors cursor-pointer group"
          onClick={() => handleProfileClick(request.user.id)}
        >
          <div className="h-10 w-10 rounded-full bg-adv-darkGray flex items-center justify-center mr-3">
            {request.user.avatar_url ? (
              <img
                src={request.user.avatar_url}
                alt={request.user.username}
                className="h-full w-full rounded-full object-cover"
              />
            ) : (
              <User size={20} className="text-adv-lightGray" />
            )}
          </div>
          <div className="flex-1">
            <p className="font-medium text-white group-hover:text-adv-orange transition-colors">
              {request.user.first_name} {request.user.last_name}
            </p>
            <p className="text-sm text-adv-lightGray">
              Requested {new Date(request.created_at).toLocaleDateString()}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              className="text-green-400 hover:text-green-300 hover:bg-green-500/10 border-green-500/30 p-2"
              onClick={(e) => handleApprove(e, request.id)}
              disabled={processingId === request.id}
            >
              {processingId === request.id && actionType === 'approve' ? (
                <Loader2 size={16} className="animate-spin" />
              ) : (
                <Check size={16} />
              )}
            </Button>
            <Button
              variant="outline"
              className="text-red-400 hover:text-red-300 hover:bg-red-500/10 border-red-500/30 p-2"
              onClick={(e) => handleDecline(e, request.id)}
              disabled={processingId === request.id}
            >
              {processingId === request.id && actionType === 'decline' ? (
                <Loader2 size={16} className="animate-spin" />
              ) : (
                <X size={16} />
              )}
            </Button>
            <ExternalLink size={14} className="text-adv-lightGray group-hover:text-adv-orange transition-colors" />
          </div>
        </div>
      ))}
    </div>
  );
}
